import { useState, type CSSProperties } from 'react';
import type { BuildingType, BuildingZone, PlayerRace } from '@kaeliux/shared';
import { BUILDING_DEFINITIONS } from '@kaeliux/shared';
import { LevelBadge } from '../ui/LevelBadge';
import { TileProgressCluster, type BuildingUpgradeProgress } from './TileProgressCluster';
import { getBuildingIcon } from '../../utils/buildingIcons';
import { zoneAccentStyle } from './zoneAccent';
import { computeShadowGeometry, containedSpriteWidth, castShadowTransform } from './buildingShadow';
import { measureSpriteBase, type SpriteBase } from './spriteBase';
import { BUILDING_SPRITE_WIDTH as SPRITE_WIDTH, BUILDING_SPRITE_HEIGHT as SPRITE_HEIGHT } from './buildingSpriteSize';

export type { BuildingUpgradeProgress };

export interface BuildingTileProps {
    id: string;
    buildingType: BuildingType;
    zone: BuildingZone;
    /**
     * Current level, always >= 1 — `PlanetSurface` (DS-25) only ever mounts
     * this component for a built position; a level-0 position renders
     * `PlotTile` instead.
     */
    level: number;
    race: PlayerRace | undefined;
    /** Absolute position/stacking supplied by the parent (`projectTile` output + worldX). */
    style?: CSSProperties;
    /** Present while an upgrade is in progress; omit for a settled building. */
    upgrade?: BuildingUpgradeProgress;
}

const ROOT_CLASSES = 'absolute flex flex-col items-center';

const SPRITE_CLASSES = 'relative flex max-h-[194px] max-w-[178px] items-end [filter:contrast(1.06)_saturate(1.04)] drop-shadow-sprite';

/**
 * Contact shadow cast on the ground under the sprite. The sprites are
 * transparent-background PNGs, so the tile needs a real shadow to sit them on
 * the ground — the earlier zone-accent halo read as a glow behind the
 * building rather than as ground contact. A radial gradient rather than a
 * flat fill so the shadow fades out at its rim instead of ending on a hard
 * edge, darkest directly under the sprite's base.
 *
 * Sized from the sprite's own footprint (DS-26, `buildingShadow.ts`) rather
 * than a fixed pixel size — the previous constant width/height couldn't sit
 * under every building's very different footprint. `-translate-x-1/2` is
 * applied via inline `transform` instead of a class here, so a building's
 * `offsetX` (for sprites whose mass sits off-centre) can compose with it.
 */
const GROUND_SHADOW_CLASSES = 'pointer-events-none absolute left-1/2 -z-10';

const GROUND_SHADOW_BACKGROUND =
    'radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.34) 55%, rgba(0,0,0,0) 78%)';

const GROUND_SHADOW_BLUR = 'blur(5px)';

/**
 * The projected sprite silhouette. `brightness(0)` blackens every pixel while
 * leaving alpha alone, so the shadow is the building's real outline rather
 * than an approximation of it.
 */
const CAST_SHADOW_CLASSES =
    'pointer-events-none absolute bottom-0 left-1/2 -z-10 origin-bottom [filter:brightness(0)_blur(4px)] opacity-70';

const NAME_CLASSES = 'mt-2 text-body font-bold text-content-primary';

const NAME_STYLE: CSSProperties = { textShadow: '0 1px 5px rgba(0,0,0,.95)' };

/**
 * One building standing on the planet surface (DS-15). Level, sprite and
 * upgrade progress are all supplied by the caller (`PlanetSurface`, reading
 * `usePlanetStore().currentPlanet.buildings` + `utils/buildQueue.ts`) — this
 * component is a pure renderer so it stays trivially unit-testable.
 */
export function BuildingTile({ id, buildingType, zone, level, race, style, upgrade }: BuildingTileProps) {
    const definition = BUILDING_DEFINITIONS[buildingType];
    const iconSrc = getBuildingIcon(buildingType, level, race);
    // Sprites are letterboxed by `object-contain`, so the rendered width is
    // only the box width for those wide enough to fill it. Measure the loaded
    // image and size the shadow off that; until it loads, fall back to the box.
    const [spriteNaturalSize, setSpriteNaturalSize] = useState<{ width: number; height: number } | null>(null);
    const [spriteBase, setSpriteBase] = useState<SpriteBase | null>(null);
    const renderedSpriteWidth = spriteNaturalSize
        ? containedSpriteWidth(spriteNaturalSize.width, spriteNaturalSize.height, SPRITE_WIDTH, SPRITE_HEIGHT)
        : null;
    const shadowGeometry = computeShadowGeometry(buildingType, renderedSpriteWidth ?? SPRITE_WIDTH);

    // Anchor both shadows on the sprite's measured contact base rather than
    // the bottom centre of its box: these buildings stand off-centre, and some
    // sprites carry empty space below them, which is what left them looking
    // like they hovered above their own shadow.
    const spriteWidth = renderedSpriteWidth ?? SPRITE_WIDTH;
    const spriteHeight = spriteNaturalSize && renderedSpriteWidth
        ? renderedSpriteWidth * (spriteNaturalSize.height / spriteNaturalSize.width)
        : SPRITE_HEIGHT;
    const baseOffsetX = spriteBase ? (spriteBase.centerX - 0.5) * spriteWidth : 0;
    const baseLift = spriteBase ? (1 - spriteBase.bottomY) * spriteHeight : 0;
    const measuredBaseWidth = spriteBase ? spriteBase.width * spriteWidth : null;

    return (
        <div
            data-tile-id={id}
            className={ROOT_CLASSES}
            style={{ transformOrigin: 'bottom center', ...zoneAccentStyle(zone), ...style }}
        >
            <div className={SPRITE_CLASSES}>
                <div
                    data-testid={`building-tile-shadow-${id}`}
                    aria-hidden="true"
                    className={GROUND_SHADOW_CLASSES}
                    style={{
                        width: measuredBaseWidth ?? shadowGeometry.width,
                        height: (measuredBaseWidth ?? shadowGeometry.width) * (shadowGeometry.height / shadowGeometry.width),
                        bottom: baseLift - 8,
                        transform: `translateX(calc(-50% + ${baseOffsetX + (measuredBaseWidth ? 0 : shadowGeometry.offsetX)}px))`,
                        background: GROUND_SHADOW_BACKGROUND,
                        filter: GROUND_SHADOW_BLUR,
                    }}
                />
                <img
                    src={iconSrc}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                    className={CAST_SHADOW_CLASSES}
                    style={{
                        width: spriteWidth,
                        bottom: baseLift,
                        transform: `translateX(${baseOffsetX}px) ${castShadowTransform()}`,
                    }}
                />
                <img
                    src={iconSrc}
                    alt={definition.name}
                    width={SPRITE_WIDTH}
                    height={SPRITE_HEIGHT}
                    loading="eager"
                    draggable={false}
                    onLoad={(event) => {
                        setSpriteNaturalSize({
                            width: event.currentTarget.naturalWidth,
                            height: event.currentTarget.naturalHeight,
                        });
                        setSpriteBase(measureSpriteBase(event.currentTarget));
                    }}
                    className="block h-auto w-auto max-h-[194px] max-w-[178px] object-contain"
                />
                <TileProgressCluster upgrade={upgrade}>
                    <LevelBadge level={level} />
                </TileProgressCluster>
            </div>
            <span className={NAME_CLASSES} style={NAME_STYLE}>
                {definition.name}
            </span>
        </div>
    );
}

export default BuildingTile;
